import { Calendar, Popover, Tooltip } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CalendarArrow } from "@/Components/Icons/CalendarArrow";
import {
  fetchShiftCalendar,
  setShiftCalendarLoading,
  updateShiftScheduleFilter,
  toggleShowShiftScheduleModal,
  updateRightShiftScheduleFilter,
} from "@/reducers/clientdashboard";

import { ShiftCalendarPopups } from "./ShiftCalendarPopups";
import { ShiftScheduleTypesList } from "./ShiftScheduleTypesList";
import { TOOLTIPTEXT } from "../../../../../utilities/CommonText";
import ButtonLoader from "../../../../ButtonLoader";
import ExpandIcon from "../../../../Icons/chat/ExpandIcon";
import DashboardShiftScheduleModal from "../../../../modals/DashboardShiftScheduleModal";

/**
 * Fetches shift data from the server for the active calendar month and updates the shift schedule.
 *
 * @return {void}
 */
export const ShiftCalendar = () => {
  const { shiftCalendar, shiftScheduleFilter, shiftCalendarLoading } =
    useSelector((state) => state.clientDashboard);
  const dispatch = useDispatch();
  const { isPremiumAccount } = useSelector((state) => state.session);
  const [activeCalender, setActiveCalender] = useState([
    dayjs(),
    dayjs().add(1, "month"),
  ]);
  const [activeMonth, setActiveMonth] = useState(0);

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    dispatch(setShiftCalendarLoading(true));
    dispatch(
      fetchShiftCalendar({
        date: activeCalender[activeMonth].format("YYYY-MM-DD"),
      }),
    );
  }, [activeMonth, shiftScheduleFilter.shift_status, activeCalender]);

  useEffect(() => {
    dispatch(setShiftCalendarLoading(true));
    dispatch(
      fetchShiftCalendar({
        date: activeCalender[activeMonth].format("YYYY-MM-DD"),
      }),
    );
  }, []);

  useEffect(() => {
    if (!isPremiumAccount) {
      dispatch(
        updateShiftScheduleFilter({
          shift_status: "booked",
        }),
      );
    }
  }, [isPremiumAccount]);

  /**
   * A function that takes a month index and returns the corresponding month name abbreviation.
   *
   * @param {number} monthIndex - The index of the month (0-11).
   * @return {string} The name of the month abbreviation.
   */
  const getMonthName = (monthIndex) => {
    let monthName = moment().month(monthIndex).format("MMM");
    return monthName;
  };
  return (
    <div className="ShiftCalendar_Wrapper tour-dashboard-shift-schedule !font-openSans">
      <div className="tour-dashboard-shift-schedule w-full min-w-0 max-w-[550px] rounded-[10px] bg-white p-4 pb-2 shadow-[0px_x1px_6px_0px_#E7EFFB] lg:max-w-[371px]">
        <div className="mb-[15px] flex justify-between">
          <div>
            <h3 className="text-[18px] font-semibold !leading-[normal]">
              Shift Schedule
            </h3>
          </div>
          <Tooltip title={TOOLTIPTEXT.expandCalendar}>
            <button
              onClick={() => {
                dispatch(toggleShowShiftScheduleModal(true));
              }}
            >
              <ExpandIcon cls={"text-primary"} />
            </button>
          </Tooltip>
          <DashboardShiftScheduleModal />
        </div>
        <div className="flex w-full flex-col gap-6 sm:max-lg:flex-row">
          <ShiftScheduleTypesList />
          <div className="relative w-auto md:max-w-[340px]">
            {shiftCalendarLoading ? (
              <div className="absolute bottom-0 left-0 z-10 flex h-[calc(100%-72px)] w-full items-center justify-center bg-[rgb(255,255,255,0.5)] backdrop-blur-sm">
                <span className="text-[16px] font-semibold text-black">
                  <ButtonLoader text={"Please Wait.."} loadingFirst />
                </span>
              </div>
            ) : null}
            <Calendar
              fullscreen={false}
              value={activeCalender[activeMonth]}
              fullCellRender={(value) => {
                let calDataHasDate = _.has(
                  shiftCalendar,
                  moment(value.$d).format("YYYY-MM-DD"),
                );
                let isCurrentMonth =
                  activeCalender[activeMonth].month() === value.$M;
                if (calDataHasDate && isCurrentMonth) {
                  let calDateObj =
                    shiftCalendar[moment(value.$d).format("YYYY-MM-DD")];
                  let totalShiftInDate = calDateObj.length;
                  let shiftStatusObj = {};
                  calDateObj.forEach((item) => {
                    let parentObj = item.contract_status;
                    if (_.has(shiftStatusObj, parentObj)) {
                      shiftStatusObj[parentObj] = shiftStatusObj[parentObj] + 1;
                    } else {
                      shiftStatusObj[parentObj] = 1;
                    }
                  });
                  return (
                    <Popover
                      rootClassName="z-[1033]"
                      content={
                        <ShiftCalendarPopups
                          data={calDateObj}
                          shiftDate={value.$d}
                        />
                      }
                    >
                      <div
                        className="relative grid h-[48px] w-[48px] place-items-center"
                        onClick={() => {
                          dispatch(
                            updateRightShiftScheduleFilter({
                              shift_type: "today",
                              shift_date: moment(value.$d).format("YYYY-MM-DD"),
                            }),
                          );
                        }}
                      >
                        <div
                          className={`relative z-[1] h-[40px] w-[40px] bg-gradient-to-b text-[18px] font-medium leading-[40px]`}
                        >
                          <div className="absolute left-[1px] top-[1px] flex h-[38px] w-[38px] flex-col overflow-hidden rounded-lg">
                            {shiftStatusObj.Published > 0 ? (
                              <span className="block flex-1 bg-red"></span>
                            ) : null}
                            {shiftStatusObj.Applied > 0 ? (
                              <span className="block flex-1 bg-yellow"></span>
                            ) : null}
                            {shiftStatusObj.Booked > 0 ||
                            shiftStatusObj.Worked > 0 ? (
                              <span className="block flex-1 bg-green"></span>
                            ) : null}
                          </div>
                          <span
                            className={`relative ${Object.keys(shiftStatusObj).length == 0 ? "text-txtColor" : "text-white"}`}
                          >
                            {value.$D}
                          </span>
                        </div>
                      </div>
                    </Popover>
                  );
                } else {
                  return (
                    <div className="grid h-[48px] w-[48px] place-items-center">
                      <p className="h-[40px] w-[40px] text-[18px] font-medium leading-[40px] text-txtColor">
                        {value.$D}
                      </p>
                    </div>
                  );
                }
              }}
              headerRender={({ value, type, onChange, onTypeChange }) => {
                return (
                  <>
                    <div
                      className={`relative mb-[16px] grid h-[40px] grid-cols-2 overflow-hidden rounded-lg bg-grey before:absolute before:top-0 before:z-0 before:h-full before:w-1/2 before:bg-primary before:transition-all before:duration-200 before:ease-linear before:content-[''] ${
                        activeMonth === 0 ? "before:left-0" : "before:left-1/2"
                      }`}
                    >
                      <div
                        className={`current_month relative z-[1] flex items-center gap-4 p-[8px_24px_8px_12px] transition-all duration-200 ease-linear ${
                          activeMonth === 0 ? "text-white" : ""
                        }`}
                        onClick={(e) => {
                          if (
                            e.target.tagName === "BUTTON" ||
                            e.target.tagName === "svg" ||
                            e.target.tagName === "path"
                          ) {
                            return;
                          } else {
                            setActiveMonth(0);
                          }
                        }}
                      >
                        <button
                          className="flex h-[24px] w-[24px] items-center justify-center rounded-lg !bg-white !text-txtColor"
                          onClick={() => {
                            if (activeMonth === 0) {
                              setActiveMonth(1);
                              setActiveCalender([
                                activeCalender[0].subtract(2, "month"),
                                activeCalender[1].subtract(2, "month"),
                              ]);
                            } else {
                              setActiveMonth(0);
                            }
                          }}
                        >
                          <CalendarArrow />
                        </button>
                        <div className="line-clamp-1 grow text-[16px] font-medium leading-[normal]">
                          {getMonthName(activeCalender[0].month())}{" "}
                          {activeCalender[0].year()}
                        </div>
                      </div>
                      <div
                        className={`next_month relative z-[1] flex items-center gap-4 p-[8px_12px_8px_24px] transition-all duration-200 ease-linear ${
                          activeMonth === 1 ? "text-white" : ""
                        }`}
                        onClick={(e) => {
                          if (
                            e.target.tagName === "BUTTON" ||
                            e.target.tagName === "svg" ||
                            e.target.tagName === "path"
                          ) {
                            return;
                          } else {
                            setActiveMonth(1);
                          }
                        }}
                      >
                        <div className="line-clamp-1 grow text-right text-[16px] font-medium leading-[normal]">
                          {getMonthName(activeCalender[1].month())}{" "}
                          {activeCalender[1].year()}
                        </div>
                        <button
                          className="flex h-[24px] w-[24px] -rotate-180 items-center justify-center rounded-lg !bg-white !text-txtColor"
                          onClick={() => {
                            if (activeMonth === 1) {
                              setActiveMonth(0);
                              setActiveCalender([
                                activeCalender[0].add(2, "month"),
                                activeCalender[1].add(2, "month"),
                              ]);
                            } else {
                              setActiveMonth(1);
                            }
                          }}
                        >
                          <CalendarArrow />
                        </button>
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
